import { apiFetch } from '../api-client';

export type ContractStatus = 'active' | 'completed' | 'paused' | 'cancelled';
export type ContractKind = 'revenue' | 'expense';
export type ContractPayStatus = 'pending' | 'overdue' | 'paid';
export type PayHealth = 'settled' | 'overdue' | 'due_soon' | 'on_time';
export type RiskLevel = 'high' | 'medium' | 'low';

export interface ContractPayment {
  id: string;
  periodIndex: number;
  dueDate: string;
  amount: number;
  paidAt: string | null;
  status: ContractPayStatus;
}

export interface ContractDocument {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface Contract {
  id: string;
  code: string;
  kind: ContractKind;
  customer: string;
  name: string;
  value: number;
  signedAt: string;
  periodsTotal: number;
  periodsPaid: number;
  status: ContractStatus;

  customerTaxCode: string | null;
  customerAddress: string | null;
  customerContact: string | null;
  startDate: string | null;
  endDate: string | null;
  businessUnit: string | null;
  serviceType: string | null;
  salesperson: string | null;
  latePenaltyRate: number | null;
  notes: string | null;

  payments?: ContractPayment[];
  documents?: ContractDocument[];
  /** Computed server-side: tổng thể tình trạng TT. */
  payStatus?: PayHealth;
}

export interface CreateContractInput {
  code: string;
  kind?: ContractKind;
  customer: string;
  name: string;
  value: number;
  signedAt: string;
  periodsTotal: number;
  periodsPaid?: number;
  status?: ContractStatus;
  customerTaxCode?: string;
  customerAddress?: string;
  customerContact?: string;
  startDate?: string;
  endDate?: string;
  businessUnit?: string;
  serviceType?: string;
  salesperson?: string;
  latePenaltyRate?: number;
  notes?: string;
  attachmentIds?: string[];
}

export type UpdateContractInput = Partial<Omit<CreateContractInput, 'code'>>;

export interface ContractsSummary {
  activeCount: number;
  totalActiveValue: number;
  overdueCount: number;
  overdueCustomers: number;
  overdueList: Array<{ customer: string; code: string; periodIndex: number; amount: number; dueDate: string; overdueDays: number }>;
  dueSoonCount: number;
}

export interface AgingRow {
  customer: string;
  notDue: number;
  d30: number;
  d60: number;
  d90: number;
  over90: number;
  total: number;
}

export interface RiskRow {
  customer: string;
  contractCode: string;
  contractId: string;
  onTimeRate: number;
  avgLateDays: number;
  overdueCount: number;
  totalPaid: number;
  level: RiskLevel;
  hint: string;
}

export interface ClassificationUnit {
  unit: string;
  count: number;
  total: number;
  services: Array<{ name: string; count: number; total: number }>;
}

function withKind(url: string, kind?: ContractKind): string {
  return kind ? `${url}?kind=${kind}` : url;
}

export const contractsApi = {
  list: (kind?: ContractKind) => apiFetch<{ items: Contract[] }>(withKind('/api/contracts', kind)),
  summary: (kind?: ContractKind) => apiFetch<ContractsSummary>(withKind('/api/contracts/summary', kind)),
  aging: (kind?: ContractKind) => apiFetch<{ items: AgingRow[] }>(withKind('/api/contracts/aging', kind)),
  risk: (kind?: ContractKind) => apiFetch<{ items: RiskRow[] }>(withKind('/api/contracts/risk', kind)),
  classification: (kind?: ContractKind) => apiFetch<{ items: ClassificationUnit[] }>(withKind('/api/contracts/classification', kind)),
  findOne: (id: string) => apiFetch<Contract>(`/api/contracts/${id}`),
  analyzeDocument: (contractId: string, attachmentId: string) =>
    apiFetch<{ status: 'needs_ai_center' | 'ready_for_review'; attachmentId: string; fileName: string; message: string }>(
      `/api/contracts/${contractId}/documents/${attachmentId}/analyze`, { method: 'POST' },
    ),
  create: (input: CreateContractInput) =>
    apiFetch<Contract>('/api/contracts', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: UpdateContractInput) =>
    apiFetch<Contract>(`/api/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: string) => apiFetch<{ ok: true }>(`/api/contracts/${id}`, { method: 'DELETE' }),
  addPayment: (contractId: string, input: { periodIndex: number; dueDate: string; amount: number }) =>
    apiFetch<ContractPayment>(`/api/contracts/${contractId}/payments`, {
      method: 'POST', body: JSON.stringify(input),
    }),
  updatePayment: (paymentId: string, input: { dueDate?: string; amount?: number }) =>
    apiFetch<ContractPayment>(`/api/contracts/payments/${paymentId}`, {
      method: 'PATCH', body: JSON.stringify(input),
    }),
  removePayment: (paymentId: string) =>
    apiFetch<{ ok: true }>(`/api/contracts/payments/${paymentId}`, { method: 'DELETE' }),
  markPaid: (paymentId: string) =>
    apiFetch<Contract>(`/api/contracts/payments/${paymentId}/mark-paid`, { method: 'POST' }),
};
